<template>
  <div>
    <div
      :class="{
        'hyp-selector-panel-container': !isSelected,
        'hyp-selector-panel-container-active application-heading': isSelected,
        'hyp-selector-panel-disabled': disabled,
      }"
      @click="toggleDialog()"
    >
      <v-btn
        v-if="text"
        :disabled="disabled"
        color="transparent"
        depressed
        :style="{ width: LeftSidePanelWidth }"
        class="hyp-selector-btn ma-0 pa-0"
        :title="text"
      >
        <img
          v-if="imgSrc"
          :src="imgSrc"
          :alt="text"
          class="hyp-selector-panel-img mr-2"
        />

        <span class="hyp-text">{{ text }}</span>

        <slot name="append"></slot>
      </v-btn>
    </div>

    <MaintenanceModal
      v-if="maintenance.enabled && isSelected"
      :title="maintenance.title"
      :text="maintenance.text"
    ></MaintenanceModal>

    <v-dialog
      v-if="!maintenance.enabled"
      v-model="dialog"
      eager
      persistent
      no-click-animation
      :hide-overlay="hideOverlay"
    >
      <v-card
        ref="noSearchCard"
        v-if="!isSearch"
        class="pa-1 hyp-dialog-content-container hyp-dialog-content-container-full-height"
        :style="style"
      >
        <v-card-text :class="contentClass">
          <slot></slot>
        </v-card-text>
      </v-card>
      <div
        ref="searchCard"
        v-if="isSearch"
        class="pa-0 hyp-dialog-content-container hyp-dialog-content-container-full-height"
        :style="style"
      >
        <slot></slot>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import lodashGet from "lodash/get";

import { mapGetters } from "vuex";

import { GET_SELECTOR_PANEL } from "@/store/actions.type";
import {
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_PLACES,
  MODULE_PREFIX_MONITOR,
} from "@/store";
import { ICONS } from "@/common/constants/icons";
import {
  MAIN_MENU_SELECTORS,
  MAINTENANCE_TYPES,
} from "@/common/constants/home";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

import { LeftSidePanelWidth } from "@/common/constants/layout";

const EMITTED_ACTIONS = {
  OPEN: "open",
  CLICK: "click",
  RESIZED: "resized",
};
export default {
  name: "SelectorPanel",
  components: {
    MaintenanceModal: () => import("@/components/common/MaintenanceModal"),
  },
  props: {
    type: {
      type: String,
      validator: function (value) {
        return MAIN_MENU_SELECTORS.indexOf(value) !== -1;
      },
    },
    text: {
      type: String,
    },
    dialogWidth: {
      type: Number,
      default: 290,
    },
    dialogFullHeight: {
      type: Boolean,
      default: true,
    },
    contentClass: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hidePanel: {
      type: Boolean,
      default: false,
    },
    forceClose: {
      type: String,
    },
    folderType: {
      type: String,
    },
    //Navigate to this URL path when this selector is selected
    urlPath: {
      type: String,
      default: "",
    },
  },
  inject: ["leftPanel"],
  data: function () {
    return {
      dialog: false,
      dialogTop: 0,
      maintenance: {
        enabled: false,
        title: null,
        text: null,
        types: MAINTENANCE_TYPES,
      },
    };
  },
  mounted() {
    this.adjustDialogTops();

    // this checks the feature lock to see if there are any of the selectorPanel types
    // that may be in maintenance mode. If it finds a selectorPanel type that is in the
    // lock file then it checks to see if that feature is currently locked.
    if (
      this.lockedFeatures[`maintenance-${this.type}`] &&
      this.featureUnlocked(`maintenance-${this.type}`)
    ) {
      this.maintenance.enabled = true;
      this.maintenance.title = lodashGet(
        this.maintenance,
        `types[${this.type}].title`
      )
        ? this.maintenance.types[this.type].title
        : "Currently in Maintenance";
      this.maintenance.text = lodashGet(
        this.maintenance,
        `types[${this.type}].text`
      )
        ? this.maintenance.types[this.type].text
        : "This section of Seerist is currently in maintenance and will be available shortly.";
    }

    //If selected on mount, open the panel right away
    if (this.isSelected) {
      this.dialog = new Date().toISOString();
    }
  },
  updated() {
    let el = this.isSearch ? this.$refs.searchCard : this.$refs.noSearchCard;
    if (el && el.$el && el.$el.clientWidth) {
      this.$emit(EMITTED_ACTIONS.RESIZED, el.$el.clientWidth);
    }
  },
  computed: {
    ...mapGetters({
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
      places: MODULE_PREFIX_PLACES + "places",
      enabled: MODULE_PREFIX_MONITOR + "enabled",
      lockedFeatures: MODULE_PREFIX_HOME + "lockedFeatures",
    }),
    id() {
      if (this.type === "alerts") {
        return "alerts";
      } else if (this.type === "notifications") {
        return "notifications";
      } else if (this.type === "profile") {
        return "profile";
      } else if (this.type === "resources") {
        return "resources";
      } else if (this.type === "collaboration") {
        return "collaboration";
      } else if (this.type === "cyber") {
        return "cyber";
      } else if (this.type === "search") {
        return "search";
      } else if (this.type === "comms") {
        return "comms";
      } else if (this.type === "dashboard") {
        return "dashboard";
      } else if (this.type === "latest") {
        return "latest";
      } else if (this.type === "stability") {
        return "stability";
      } else if (this.type === "reporting") {
        return "reporting";
      }

      return this._uid;
    },
    isSelected() {
      if (
        this.selectorPanel.selected === this.id &&
        (this.dialog || this.id === "dashboard" || this.id === "latest")
      ) {
        return true;
      }
      return false;
    },
    isSearch() {
      return this.type === "search";
    },
    imgSrc() {
      let curType = this.type;
      if (curType === "saveSearch") {
        curType = "collaboration";
      }
      if (curType && ICONS.menu[curType]) {
        let src = ICONS.menu[curType].inactive;
        if (this.isSelected) {
          src = ICONS.menu[curType].active;
        }
        return require(`@/assets/images${src}`);
      }
      return null;
    },
    style() {
      let isImportant = "";
      if (
        this.type === "events" ||
        this.type === "influences" ||
        this.type === "places" ||
        this.type === "hotspots" ||
        this.type === "esri"
      ) {
        isImportant = " !important";
      }
      return {
        left:
          this.featureUnlocked("left-panel-mini") && this.leftPanel.mini
            ? "45px !important"
            : LeftSidePanelWidth + "!important",
        width: `${this.dialogWidth}px ${isImportant}`,
        top: this.dialogTop,
      };
    },
    hideOverlay() {
      if (
        this.type === "alerts" ||
        this.type === "collaboration" ||
        this.type === "comms" ||
        this.type === "cyber"
      ) {
        return false;
      } else if (this.type === "saveSearch") {
        if (this.folderType === "collaboration") {
          return false;
        }
      }
      return true;
    },
    LeftSidePanelWidth() {
      return LeftSidePanelWidth;
    },
    selectedPanel() {
      return this.selectorPanel.selected;
    },
  },
  methods: {
    /**
     * Opens or closes the content area that's associated with this panel (e.g. latest, watch, collaboration, etc.)
     */
    toggleDialog() {
      if (!this.disabled) {
        //Open the dialog
        if (!this.dialog) {
          this.selectorPanel.selected = this.id;
          this.$store.dispatch(
            MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
            this.selectorPanel
          );
          //Update the URL
          this.changeUrl();
          this.handlePageTrackerEvent(
            EVENT_CATEGORIES.menu,
            EVENT_ACTIONS.opened,
            this.type
          );
          this.saveMatomoTrackerPageView({ url: this.type });
        }
        //Close the dialog
        else {
          this.selectorPanel.selected = null;
          this.selectorPanel.selectedData = null;
          this.$store.dispatch(
            MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
            this.selectorPanel
          );
          this.handlePageTrackerEvent(
            EVENT_CATEGORIES.menu,
            EVENT_ACTIONS.closed,
            this.type
          );
        }

        if (this.hidePanel) {
          this.dialog = false;
        } else {
          this.dialog = !this.dialog;
        }
        this.$emit(EMITTED_ACTIONS.CLICK, this.dialog);
      }
    },
    changeUrl() {
      if (this.urlPath) {
        window.history.replaceState({}, "Seerist", this.urlPath);
      } else {
        window.history.replaceState({}, "Seerist", "/");
      }
    },
    adjustDialogTops() {
      if (this.dialogFullHeight) {
        this.dialogTop = "0";
      } else {
        this.dialogTop = "455px";
        if (this.type === "media") {
          this.dialogTop = "600px";
        }
      }
    },
  },
  watch: {
    isSelected: {
      handler() {
        this.dialog = this.selectorPanel.selected === this.id;
        if (
          this.type === "alerts" ||
          this.type === "notifications" ||
          this.type === "profile" ||
          this.type === "collaboration" ||
          this.type === "cyber" ||
          this.type === "search" ||
          this.type === "latest" ||
          this.type === "reporting"
        ) {
          this.$emit(EMITTED_ACTIONS.OPEN, this.isSelected);
        }
      },
    },
    selectorPanel: {
      handler() {
        if (
          this.selectorPanel.selected === this.id &&
          this.type === "alerts" &&
          lodashGet(this.selectorPanel, "selectedData")
        ) {
          this.toggleDialog();
        }
      },
      deep: true,
    },
    forceClose: {
      handler(prev, curr) {
        if (this.forceClose && prev !== curr) {
          this.toggleDialog();
        }
      },
    },
    enabled: {
      handler(val) {
        if (val) {
          this.dialog = false;
        }
      },
    },
    forceOpen: {
      handler(prev, curr) {
        if (this.forceOpen && prev !== curr) {
          if (this.id === "collaboration" && this.type === "savedSearch") {
            this.selectorPanel.selected = "collaboration";
          }
          this.dialog = true;
        }
      },
    },
    selectedPanel: {
      handler(val) {
        if (val === this.type && !this.isSelected) this.toggleDialog();
      },
    },
  },
};
</script>

<style>
.hyp-selector-btn .v-btn__content {
  justify-content: left !important;
  padding-left: 10px;
}
.v-dialog {
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
}
</style>

<style scoped lang="scss">
.hyp-text {
  color: black;
}
.hyp-selector-btn {
  color: black !important;
  text-transform: none !important;
  height: 30px !important;
}
.hyp-selector-panel-container-active {
  border-left: 3px solid black;
}
.hyp-dialog-content-container {
  background-color: #f7f7fa !important;
  -webkit-box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.14) !important;
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.14) !important;
  border-top-right-radius: 5px !important;
  border-bottom-right-radius: 5px !important;
  min-height: 50px !important;
  position: absolute;
  top: 0;
}
.hyp-dialog-content-container-full-height {
  bottom: 0;
  width: auto !important;
}
.hyp-selector-panel-disabled {
  opacity: 0.35;
}
.hyp-selector-panel-img {
  width: 24px;
  height: 24px;
}
</style>
